import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '../stores/AuthStore'
import { API_CAPABILITY, canAccessCapability } from '../access/apiCapabilities'
import { HOME_EXPERIENCE, resolveHomeExperience } from '../access/homeExperience'
import { PERMISSION_KEYS } from '../access/permissionKeys'

/**
 * Central access surface for UI layout and gated API calls.
 */
export function useAppCapabilities() {
  const authStore = useAuthStore()
  const { accessLoaded } = storeToRefs(authStore)

  const homeExperience = computed(() => resolveHomeExperience(authStore))

  const isAccessLoaded = computed(() => accessLoaded.value)

  const hasCoreWorkbench = computed(() => (
    accessLoaded.value && authStore.hasPermission(PERMISSION_KEYS.CORE_ACCESS)
  ))

  const showFullWorkbenchHome = computed(() => (
    homeExperience.value === HOME_EXPERIENCE.FULL
  ))

  const showContentLibraryHome = computed(() => (
    homeExperience.value === HOME_EXPERIENCE.CONTENT_LIBRARY_ONLY
  ))

  const isContentLibraryOnlyUser = computed(() => showContentLibraryHome.value)

  const isTransformOnlyUser = computed(() => (
    homeExperience.value === HOME_EXPERIENCE.TRANSFORM_ONLY
  ))

  const hasContentLibrary = computed(() => (
    accessLoaded.value && authStore.hasPermission(PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS)
  ))

  const hasTransform = computed(() => (
    accessLoaded.value && authStore.hasPermission(PERMISSION_KEYS.TRANSFORM_ACCESS)
  ))

  /**
   * Content-library-only and transform-only users share one Home layout; the individual
   * cards inside it are gated by permission rather than by experience, so there is a single
   * structure to maintain instead of one per limited-access persona.
   */
  const showLibraryStyleHome = computed(() => (
    showContentLibraryHome.value || isTransformOnlyUser.value
  ))

  function canCallApi(capability) {
    if (!accessLoaded.value) {
      return false
    }
    return canAccessCapability(authStore, capability)
  }

  return {
    homeExperience,
    HOME_EXPERIENCE,
    isAccessLoaded,
    hasCoreWorkbench,
    showFullWorkbenchHome,
    showContentLibraryHome,
    isContentLibraryOnlyUser,
    isTransformOnlyUser,
    hasContentLibrary,
    hasTransform,
    showLibraryStyleHome,
    canCallApi,
    API_CAPABILITY
  }
}
